import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "./firebase";
import { reportClientError } from "./errorReporting";

type CreateHouseholdResult = {
  householdId: string;
  householdName: string;
  alreadyCreated?: boolean;
};

function familyForm() {
  const input = document.querySelector<HTMLInputElement>('input[name="householdName"]');
  return input?.form || null;
}

function removePrivateDefault() {
  const input = document.querySelector<HTMLInputElement>('input[name="householdName"]');
  if (!input) return;
  if (input.value.trim().toLowerCase() === "the budhus") input.value = "";
  input.defaultValue = "";
  if (!input.placeholder) input.placeholder = "The Smiths";
}

function showFormError(form: HTMLFormElement, message: string) {
  let node = form.querySelector<HTMLParagraphElement>(".auth-error");
  if (!node) {
    node = document.createElement("p");
    node.className = "auth-error";
    node.setAttribute("role", "alert");
    const submit = form.querySelector("button[type='submit']");
    submit?.before(node);
  }
  node.textContent = message;
}

async function createHouseholdFromForm(form: HTMLFormElement) {
  const user = auth.currentUser;
  if (!user) throw new Error("Sign in again before creating your family.");

  const householdName = String(new FormData(form).get("householdName") || "").trim();
  if (!householdName) throw new Error("Enter a family name first.");

  const profileSnapshot = await getDoc(doc(db, "users", user.uid));
  const profile = profileSnapshot.exists() ? profileSnapshot.data() : {};
  const displayParts = String(user.displayName || "").trim().split(/\s+/).filter(Boolean);
  const firstName = String(profile.firstName || displayParts[0] || "").trim();
  const lastName = String(profile.lastName || displayParts.slice(1).join(" ") || "").trim();
  if (!firstName) throw new Error("Finish your profile before creating your family.");

  const createHousehold = httpsCallable<
    { householdName: string; firstName: string; lastName: string },
    CreateHouseholdResult
  >(functions, "createHousehold");

  await createHousehold({ householdName, firstName, lastName });
}

export function installOnboardingRecovery() {
  removePrivateDefault();
  const observer = new MutationObserver(removePrivateDefault);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener("submit", async (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form || form !== familyForm()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const submit = form.querySelector<HTMLButtonElement>("button[type='submit']");
    if (submit?.disabled) return;

    if (submit) {
      submit.disabled = true;
      submit.textContent = "Creating family…";
    }

    try {
      await createHouseholdFromForm(form);
      const url = new URL(window.location.href);
      url.searchParams.set("famdash_refresh", Date.now().toString(36));
      window.location.replace(url.toString());
    } catch (error) {
      reportClientError(error, "window");
      const raw = error instanceof Error ? error.message : "";
      const message = raw.includes("Verify your email")
        ? "Verify your email, then try creating your family again."
        : raw.includes("profile")
          ? raw
          : raw.includes("Sign in")
            ? raw
            : "We couldn’t create your family. Please try again. If it still fails, the error has been logged for us.";
      showFormError(form, message);
      if (submit) {
        submit.disabled = false;
        submit.textContent = "Create family";
      }
    }
  }, true);
}
